import { Rpc, RpcGroup } from "effect/rpc";
import { Schema } from "effect";
import {
  PatientInfoProposalRejectedSchema,
  PatientInfoUnavailableSchema,
} from "./patient-info-errors.schema";
import {
  PatientInfoReview,
  PatientInfoReviewRequest,
  PatientInfoRevokeRequest,
  PatientInfoRevokeResult,
} from "./patient-info-review.schema";
import {
  PatientInfoSubmitRequest,
  PatientInfoSubmitResult,
} from "./patient-info-submit.schema";
import {
  PatientInfoPolicySaveResponse,
  PatientInfoPolicyUpdate,
  PatientInfoPolicyView,
} from "./patient-info-policy.schema";

/**
 * The FrontDesk+ patient-information review RPC group.
 *
 * Three review operations are gated on an opaque server-issued attestation handle rather than on any
 * client-supplied identifier. Two policy operations let the PIN-protected FrontDesk+ settings screen
 * manage the same practice-wide server policy over the existing SOL-authenticated transport:
 *
 * - `FrontDeskPatientInfoReview` — activate the review (idempotently) and read the current eligible
 *   contacts plus Hausarzt. May be re-read while the capability is active; re-reading never extends
 *   the hard expiry.
 * - `FrontDeskPatientInfoSubmit` — apply one proposal and return per-field durable outcomes.
 * - `FrontDeskPatientInfoRevoke` — best-effort teardown on skip, timeout, reset, or exit.
 * - `FrontDeskPatientInfoPolicyRead` — read the practice-wide field selection.
 * - `FrontDeskPatientInfoPolicySave` — optimistically save the master and field switches.
 *
 * This group is owned by `@ascleo/frontdesk-contracts` rather than the Bridge so the shared kiosk
 * client can import it without pulling backend runtime code into the browser. Handlers stay in
 * `@ascleo/doc-cirrus-bridge`.
 *
 * Every operation may fail with the single non-enumerating `PatientInfoUnavailable` rejection, which
 * carries no hint about which binding failed or whether a patient exists.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { FrontDeskPatientInfoRpcs } from "@ascleo/frontdesk-contracts/patient-info/rpc";
 *
 * const requestNames = FrontDeskPatientInfoRpcs.requests.size;
 * ```
 */
export class FrontDeskPatientInfoRpcs extends RpcGroup.make(
  Rpc.make("FrontDeskPatientInfoReview", {
    success: PatientInfoReview,
    error: PatientInfoUnavailableSchema,
    payload: PatientInfoReviewRequest,
  }),

  Rpc.make("FrontDeskPatientInfoSubmit", {
    success: PatientInfoSubmitResult,
    error: Schema.Union(
      [PatientInfoUnavailableSchema,
      PatientInfoProposalRejectedSchema],
    ),
    payload: PatientInfoSubmitRequest,
  }),

  Rpc.make("FrontDeskPatientInfoRevoke", {
    success: PatientInfoRevokeResult,
    error: PatientInfoUnavailableSchema,
    payload: PatientInfoRevokeRequest,
  }),

  Rpc.make("FrontDeskPatientInfoPolicyRead", {
    success: PatientInfoPolicyView,
    payload: Schema.Struct({}),
  }),

  Rpc.make("FrontDeskPatientInfoPolicySave", {
    success: PatientInfoPolicySaveResponse,
    payload: PatientInfoPolicyUpdate,
  }),
) {}
